import React, { useRef, useState } from 'react';

import { AuthContext } from './AuthContext';
import { createAuthClient } from './client';
import type { AuthClient } from './client/AuthClient';
import {
  DEFAULT_AUTH_ENDPOINT,
  DEFAULT_AUTH_LOGOUT_ENDPOINT,
  DEFAULT_AUTH_TOKEN_ENDPOINT,
  REFRESH_TOKEN
} from './client/constants';
import * as localStorage from './client/storage/localStorage';
import { getUrlParam } from './client/util/utils';
import type { AuthConfiguration, AuthProviderProps, AuthUser } from './types';

export const AuthProvider = ({
  children,
  skipAuth,
  staticAuth,
  disableRefreshAuth
}: AuthProviderProps) => {
  const isAuthenticatedRef = useRef(false);
  const [isAuthenticated, setIsAuthenticatedState] = useState(false);
  const [user, setUser] = useState<AuthUser>();
  const [authClientRef, setAuthClientRef] = useState<AuthClient>();

  const setIsAuthenticated = (value: boolean) => {
    console.log(`Setting isAuthenticated to ${value}`);
    isAuthenticatedRef.current = value;
    setIsAuthenticatedState(value);
  };

  const isSkipAuth = () => skipAuth === true;

  const isStaticAuth = () =>
    staticAuth === true && !!getUrlParam('static_access_token');

  const isSkipOrStaticAuth = () => isSkipAuth() || isStaticAuth();

  const requestAuth = async () => {
    if (
      isAuthenticatedRef.current ||
      isSkipOrStaticAuth() ||
      getUrlParam('code')
    ) {
      return;
    }

    window.authClient?.loginWithRedirect().catch((e: Error) => {
      console.error(`loginWithRedirect Error: ${e.message}`);
    });
  };

  const applyStaticAuth = (authClient: AuthClient, dummy_user: AuthUser) => {
    authClient.handleStaticAccessToken(getUrlParam('static_access_token')!);
    const user = authClient.getUser();
    setUser({
      ...user,
      given_name: user.given_name ?? dummy_user.given_name,
      family_name: user.family_name ?? dummy_user.family_name,
      name: user.name ?? dummy_user.name
    });
    setIsAuthenticated(authClient.isAuthenticated());
  };

  const applyRedirectCallback = async (authClient: AuthClient, config: AuthConfiguration) => {
    try {
      await authClient.handleRedirectCallback();
      const user = authClient.getUser();
      setUser(user);
      setIsAuthenticated(authClient.isAuthenticated());
    } catch (e: any) {
      if (e?.message?.indexOf('Invalid state') > -1) {
        window.location.replace(config.authRedirectUri || window.location.origin);
        return;
      }
      console.error(`AuthProvider redirect callback error: ${e.message}`);
    }
  };

  const applyRefreshToken = async (authClient: AuthClient, config: AuthConfiguration) => {
    try {
      await authClient.handleRefreshOnInit();
      const user = authClient.getUser();
      setUser(user);
      setIsAuthenticated(authClient.isAuthenticated());
    } catch (e: any) {
      console.error(`Error during refresh token authentication: ${e.message}`);
      if (e?.message?.indexOf('Invalid state') > -1) {
        window.location.replace(config.authRedirectUri || window.location.origin);
        return;
      }
      localStorage.removeItem(REFRESH_TOKEN);
      console.error(`AuthProvider refresh error - cleared invalid token: ${e.message}`);
    }
  };

  const initAuth = async (config = window.configuration) => {
    const dummy_user: AuthUser =
      config.authDummyUser && Object.keys(config.authDummyUser).length > 0
        ? config.authDummyUser
        : {
            given_name: 'Max',
            family_name: 'Mustermann',
            name: 'ab123'
          };

    if (isSkipAuth()) {
      setUser(dummy_user);
      setIsAuthenticated(true);
      return;
    }

    const authClient = await createAuthClient({
      issuer: config.authIssuer,
      authEndpoint:
        (config.authServerUrl || config.authIssuer) +
        (config.authEndpoint || DEFAULT_AUTH_ENDPOINT),
      response_type: config.authResponseType || 'code',
      response_mode: config.authResponseMode || 'query',
      tokenEndpoint:
        (config.authServerUrl || config.authIssuer) +
        (config.authTokenEndpoint || DEFAULT_AUTH_TOKEN_ENDPOINT),
      logoutEndpoint:
        (config.authServerUrl || config.authIssuer) +
        (config.authLogoutEndpoint || DEFAULT_AUTH_LOGOUT_ENDPOINT),
      redirect_uri: config.authRedirectUri || window.location.origin,
      client_id: config.authClientId,
      scope:
        config.authScope ||
        'openid profile' + (config.authRole ? ' ' + config.authClientId : ''),
      requiredRole: config.authRole
        ? config.authClientId + '-' + config.authRole
        : null
    });

    setAuthClientRef(authClient);
    window.authClient = authClient;

    if (isStaticAuth()) {
      applyStaticAuth(authClient, dummy_user);
    } else if (getUrlParam('code')) {
      await applyRedirectCallback(authClient, config);
    } else if (!disableRefreshAuth && localStorage.getItem(REFRESH_TOKEN)) {
      await applyRefreshToken(authClient, config);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        initAuth,
        requestAuth,
        logoutWithRedirect: () => authClientRef?.logout(),
        isAuthenticated,
        user
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
